import type { DashboardDailyMessagesViewModel } from '@/shared/api/generated/types/DashboardDailyMessagesViewModel';
import type { DashboardDailyTokensViewModel } from '@/shared/api/generated/types/DashboardDailyTokensViewModel';
import type { TutorAIDailyUsageViewModel } from '@/shared/api/generated/types/TutorAIDailyUsageViewModel';
import type { TutorAIDashboardReportViewModel } from '@/shared/api/generated/types/TutorAIDashboardReportViewModel';
import type { TutorAIDocumentSummaryViewModel } from '@/shared/api/generated/types/TutorAIDocumentSummaryViewModel';
import type { TutorAIMonthlyHistoryViewModel } from '@/shared/api/generated/types/TutorAIMonthlyHistoryViewModel';
import type { TutorAITokenBreakdownViewModel } from '@/shared/api/generated/types/TutorAITokenBreakdownViewModel';
import type { TutorAITokenUsageReportViewModel } from '@/shared/api/generated/types/TutorAITokenUsageReportViewModel';

import type {
  TutorDailyMessages,
  TutorDailyTokens,
  TutorDailyUsage,
  TutorDashboardReport,
  TutorDocument,
  TutorMonthlyHistory,
  TutorStudentUsage,
  TutorTokenBreakdown,
} from '../types/tutorAi';

/** Texto garantido: `null`/`undefined` viram string vazia. */
function text(value: string | null | undefined): string {
  return value ?? '';
}

/** Número garantido: `null`/`undefined` viram zero. */
function count(value: number | null | undefined): number {
  return value ?? 0;
}

/** Booleano garantido: `undefined` vira `false`. */
function flag(value: boolean | undefined): boolean {
  return value ?? false;
}

/**
 * Normaliza um documento de contexto.
 *
 * Status ausente é tratado como `Pending` (1) — é o estado em que a API cria o registro,
 * então é a suposição segura: mostra o documento como não indexado em vez de sugerir que
 * ele já participa das respostas do tutor.
 *
 * @param vm - ViewModel gerado, com todos os campos opcionais
 * @returns Documento sem opcionais, pronto para a UI
 *
 * @example
 * ```ts
 * normalizeDocument({ id: 7 }).status; // 1 (Pending)
 * ```
 */
export function normalizeDocument(vm: TutorAIDocumentSummaryViewModel): TutorDocument {
  return {
    id: count(vm.id),
    fileName: text(vm.fileName),
    originalFileName: text(vm.originalFileName),
    status: vm.status ?? 1,
    sizeBytes: count(vm.sizeBytes),
    chunkCount: count(vm.chunkCount),
    personId: count(vm.personId),
    personName: text(vm.personName),
    personEmail: text(vm.personEmail),
    createdAt: text(vm.createdAt),
    updatedAt: text(vm.updatedAt),
  };
}

function normalizeDailyTokens(vm: DashboardDailyTokensViewModel): TutorDailyTokens {
  return { date: text(vm.date), tokensUsed: count(vm.tokensUsed) };
}

function normalizeDailyMessages(vm: DashboardDailyMessagesViewModel): TutorDailyMessages {
  return { date: text(vm.date), messages: count(vm.messages) };
}

/**
 * Normaliza o consolidado agregado do dashboard, incluindo as duas séries diárias —
 * `Normalized<T>` só garante que as coleções existam, não os campos dos itens.
 *
 * @param vm - ViewModel gerado
 * @returns Os seis indicadores com zero no lugar de ausente, e as duas séries sempre
 * presentes como array
 */
export function normalizeDashboardReport(
  vm: TutorAIDashboardReportViewModel,
): TutorDashboardReport {
  return {
    periodStart: text(vm.periodStart),
    periodEnd: text(vm.periodEnd),
    classStudentId: count(vm.classStudentId),
    totalTokensUsed: count(vm.totalTokensUsed),
    tokensPerStudent: count(vm.tokensPerStudent),
    tokensPerConversation: count(vm.tokensPerConversation),
    totalConversations: count(vm.totalConversations),
    totalMessages: count(vm.totalMessages),
    totalStudentsWithConversations: count(vm.totalStudentsWithConversations),
    dailyTokensEvolution: (vm.dailyTokensEvolution ?? []).map(normalizeDailyTokens),
    dailyMessagesEvolution: (vm.dailyMessagesEvolution ?? []).map(normalizeDailyMessages),
  };
}

function normalizeDailyUsage(vm: TutorAIDailyUsageViewModel): TutorDailyUsage {
  return { date: text(vm.date), tokensUsed: count(vm.tokensUsed) };
}

function normalizeBreakdown(vm: TutorAITokenBreakdownViewModel): TutorTokenBreakdown {
  return {
    provider: text(vm.provider),
    operation: text(vm.operation),
    tokensUsed: count(vm.tokensUsed),
  };
}

function normalizeMonthlyHistory(vm: TutorAIMonthlyHistoryViewModel): TutorMonthlyHistory {
  return { yearMonth: text(vm.yearMonth), tokensUsed: count(vm.tokensUsed) };
}

/**
 * Normaliza o relatório de uso e limites de um aluno, incluindo as três coleções
 * aninhadas — `Normalized<T>` só garante que elas existam, não os campos dos itens.
 *
 * @param vm - ViewModel gerado
 * @returns Relatório pronto para a UI, com arrays sempre presentes
 */
export function normalizeStudentUsage(vm: TutorAITokenUsageReportViewModel): TutorStudentUsage {
  return {
    personId: count(vm.personId),
    periodStart: text(vm.periodStart),
    periodEnd: text(vm.periodEnd),
    periodTokensUsed: count(vm.periodTokensUsed),
    currentMonth: text(vm.currentMonth),
    dailyTokensUsed: count(vm.dailyTokensUsed),
    dailyLimit: count(vm.dailyLimit),
    weeklyTokensUsed: count(vm.weeklyTokensUsed),
    weeklyLimit: count(vm.weeklyLimit),
    monthlyTokensUsed: count(vm.monthlyTokensUsed),
    monthlyLimit: count(vm.monthlyLimit),
    monthlyPercentUsed: count(vm.monthlyPercentUsed),
    limitEnabled: flag(vm.limitEnabled),
    dailyLimitReached: flag(vm.dailyLimitReached),
    weeklyLimitReached: flag(vm.weeklyLimitReached),
    monthlyLimitReached: flag(vm.monthlyLimitReached),
    breakdown: (vm.breakdown ?? []).map(normalizeBreakdown),
    dailyUsage: (vm.dailyUsage ?? []).map(normalizeDailyUsage),
    history: (vm.history ?? []).map(normalizeMonthlyHistory),
  };
}
